import { Button } from '@teach/ui'
import { formatServerTime } from '@/features/ai/lib/serverTime'
import type { AnswerGateFailure } from '@/features/ai/useAnswerGate'
import type { Gate } from '@/features/ai/studioTypes'
import { useMessages } from '@/i18n'
import { messages } from './chatMessages'

interface Props {
  gate: Gate
  onAnswer: (optionKey: string) => void
  /** Ответ летит на сервер: варианты гасим, иначе второй клик выглядит как
   *  смена выбора, а сервер оставит ПЕРВЫЙ (он идемпотентен по гейту). */
  isAnswering: boolean
  /**
   * Отказ последнего клика (Important 1, финальное ревью). `answerGate` в
   * `SILENT_ENDPOINTS` — глобальный тост поверх соврал бы сырым JSON
   * (`{"retry":false,"code":"gate_expired",...}`) или молчал бы вовсе
   * (422 без строки в словаре `errorToastMiddleware`), поэтому объясняет
   * карточка. `expired`/`run_closed` строки не получают: это не неудача
   * клика, а состояние — следующий тик поллера сам поменяет `gate.state`.
   */
  gateFailure: AnswerGateFailure | null
}

const gateFailureText = (
  m: { gateForbidden: string; gateNotFound: string; gateOther: string },
  failure: AnswerGateFailure,
): string | undefined => {
  const texts: Partial<Record<AnswerGateFailure, string>> = {
    forbidden: m.gateForbidden,
    not_found: m.gateNotFound,
    other: m.gateOther,
  }
  return texts[failure]
}

/**
 * Вопрос прогона. Форма отрисовки определяется НАЛИЧИЕМ картинок у вариантов,
 * а не полем `kind`: `kind` — свободная строка сервера, и новый вид вопроса не
 * должен требовать правки фронта.
 *
 * Состояние берём из `gate.state` (его считает сервер), а `expires_at`
 * печатаем как срок — своих выводов по часам браузера не делаем. Разбирает
 * метку `formatServerTime`: поля гейта приходят без зоны, и голый `new Date`
 * сдвинул бы срок на часовой пояс автора.
 */
export const GateCard = ({ gate, onAnswer, isAnswering, gateFailure }: Props) => {
  const m = useMessages(messages)
  const disabled = gate.state !== 'pending' || isAnswering
  const chosen = gate.answer?.option_key ?? null
  const hasTiles = gate.options.some((o) => Boolean(o.image_url))
  const deadline = formatServerTime(gate.expires_at)
  const failureText =
    gate.state === 'pending' && gateFailure ? gateFailureText(m, gateFailure) : undefined

  return (
    <div className="max-w-[85%] rounded-2xl rounded-tl-md border border-border bg-muted/30 p-3.5 text-sm [overflow-wrap:anywhere]">
      <p className="mb-3 text-foreground">{gate.question}</p>

      <div className={hasTiles ? 'grid grid-cols-2 gap-2' : 'flex flex-col gap-2'}>
        {gate.options.map((option) => {
          const label = option.label ?? option.key
          const picked = chosen === option.key
          return option.image_url ? (
            <button
              key={option.key}
              type="button"
              disabled={disabled}
              onClick={() => onAnswer(option.key)}
              aria-label={label}
              className={[
                'overflow-hidden rounded-xl border text-left transition',
                picked ? 'border-primary ring-2 ring-primary' : 'border-border',
                disabled ? 'opacity-60' : 'hover:border-primary',
              ].join(' ')}
            >
              <img
                src={option.image_url}
                alt={label}
                className="aspect-video w-full object-cover"
              />
              <span className="block px-2 py-1.5 text-xs text-foreground">{label}</span>
            </button>
          ) : (
            <Button
              key={option.key}
              type="button"
              variant={picked ? 'default' : 'outline'}
              disabled={disabled}
              onClick={() => onAnswer(option.key)}
            >
              {label}
            </Button>
          )
        })}
      </div>

      {/* M4 (финальное ревью): контракт допускает вопрос БЕЗ вариантов, где
          ответ ждётся текстом (`allows_text`) — интерфейс текстовый ввод не
          рисует (за рамками части II). Без этой строки экран — пустая
          карточка с вопросом и сроком, и то, чем ответить, не видно вовсе:
          молчание хуже честного «интерфейс пока не умеет». Условие читает
          `allows_text` явно (M-1, финальное ревью), а не только «вариантов
          нет»: «нет вариантов — значит текст» было бы догадкой поверх
          контракта, а не его чтением. */}
      {gate.options.length === 0 && gate.allows_text ? (
        <p className="mt-3 text-xs text-muted-foreground">{m.gateTextOnly}</p>
      ) : null}
      {gate.state === 'pending' && deadline ? (
        <p className="mt-3 text-xs text-muted-foreground">{m.gateDeadline(deadline)}</p>
      ) : null}
      {gate.state === 'answered' && hasTiles ? (
        // Бэк отвергнутых кандидатов не удаляет никогда — сказать об этом
        // словами требует контракт: иначе автор думает, что они пропали.
        <p className="mt-3 text-xs text-muted-foreground">{m.gateRejectedKept}</p>
      ) : null}
      {gate.state === 'expired' ? (
        <p className="mt-3 text-xs text-destructive">{m.gateExpired}</p>
      ) : null}
      {failureText ? <p className="mt-3 text-xs text-destructive">{failureText}</p> : null}
    </div>
  )
}
